/**
 * Normalizes Five9 SDK payloads into the app's call model.
 *
 * @typedef {import('src/type-defs/five9Types').Five9Call} Five9Call
 */

import { looksLikeCallSid } from 'src/utils/five9/callSidValidation';

/**
 * @param {Record<string, any>|null|undefined} obj
 * @returns {{ callSid: string|null, callId: string|null, sessionId: string|null }}
 */
const readIdentifiers = (obj) => {
  if (!obj) return { callSid: null, callId: null, sessionId: null };
  const callSid = obj.callSid || obj.interactionId || obj.id || obj.callId || null;
  const callId = obj.callId || null;
  const sessionId = obj.sessionId || null;

  return {
    callSid: looksLikeCallSid(callSid) ? String(callSid) : null,
    callId: callId ? String(callId) : null,
    sessionId: sessionId ? String(sessionId) : null
  };
};

/**
 * @param {Record<string, any>|null|undefined} obj
 * @returns {{ callId: string|null, sessionId: string|null }}
 */
const readCavListIdentifiers = (obj) => {
  const list = obj?.cavList;
  if (!Array.isArray(list)) return { callId: null, sessionId: null };

  let callId = null;
  let sessionId = null;

  for (let i = 0; i < list.length; i += 1) {
    const entry = list[i];
    if (!entry) continue;

    if (entry.name === 'call_id' && entry.value) callId = String(entry.value);
    else if (entry.name === 'session_id' && entry.value) sessionId = String(entry.value);

    if (callId && sessionId) break;
  }

  return { callId, sessionId };
};

const AGENT_URI_PATTERN = /[:@]/;

/**
 * @param {Partial<Five9Call>|null|undefined} call
 * @returns {string|null}
 */
export const getCallerPhoneNumber = (call) => {
  const candidate = [call?.phoneNumber, call?.number, call?.ani]
    .find((value) => value && !AGENT_URI_PATTERN.test(String(value)));

  return candidate ? String(candidate) : null;
};

/**
 * @param {Record<string, any>} source
 * @returns {'OUTBOUND'|'INBOUND'|null}
 */
const normalizeDirection = (source) => {
  const raw = String(
    source?.direction || source?.callDirection || source?.callType || ''
  ).toUpperCase();

  if (raw.includes('OUTBOUND') || raw === 'AGENT') return 'OUTBOUND';
  if (raw.includes('INBOUND') || raw === 'CUSTOMER') return 'INBOUND';
  return null;
};

export const extractScreenpopCore = (candidates) => candidates.reduce((result, candidate) => {
  if (!Array.isArray(candidate?.cavList)) return result;

  candidate.cavList.forEach((entry) => {
    const name = typeof entry?.name === 'string' ? entry.name.trim() : '';
    if (name.toLowerCase().startsWith('screenpop_core')) result[name] = entry;
  });

  return result;
}, {});

/**
 * @param {Record<string, any>|null|undefined} obj
 * @returns {boolean}
 */
const looksLikeCall = (obj) => {
  if (!obj) return false;

  return !!(
    obj.callState
    || obj.state
    || obj.callType
    || obj.callId
    || obj.ANI
    || obj.ani
    || obj.DNIS
    || obj.dnis
    || Array.isArray(obj.cavList)
  );
};

/**
 * @param {*} payload
 * @returns {Pick<Five9Call, 'callSid'|'callId'|'sessionId'|'phoneNumber'|'ani'|'dnis'|'state'|'raw'>|null}
 */
export default function extractCall(payload) {
  let root = Array.isArray(payload) ? payload[0] : payload;
  if (root && typeof root === 'object') {
    if (root.args && Array.isArray(root.args) && root.args[0]) [root] = root.args;
    else if (root.payload && typeof root.payload === 'object') root = root.payload;
    else if (root.data && typeof root.data === 'object' && (root.data.callData || root.data.interactionData)) {
      root = root.data;
    }
  }
  if (!root || typeof root !== 'object') return null;

  const candidates = [
    root.callData,
    root.interactionData,
    root.interactionSearchData,
    root.interactionLogData,
    root.callLogData,
    root
  ].filter((c) => c && typeof c === 'object');
  const screenpopCore = extractScreenpopCore(candidates);

  let source = null;
  let ids = { callSid: null, callId: null, sessionId: null };

  for (let i = 0; i < candidates.length; i += 1) {
    const cand = candidates[i];
    const flat = readIdentifiers(cand);
    const fromCav = readCavListIdentifiers(cand);
    const merged = {
      callSid: flat.callSid,
      callId: flat.callId || fromCav.callId,
      sessionId: flat.sessionId || fromCav.sessionId
    };

    if (merged.callSid && looksLikeCall(cand)) {
      source = cand;
      ids = merged;
      break;
    }
  }

  if (!ids.callSid || !source) return null;

  const five9InteractionId = source.interactionId || root.interactionId || null;
  const five9CallId = source.callId || root.callId || null;
  const five9SessionId = source.sessionId || root.sessionId || null;
  const phoneNumber = root.interactionSearchData?.phoneNumber || source.phoneNumber || null;

  return {
    callSid: ids.callSid,
    callId: ids.callId,
    sessionId: ids.sessionId,
    ...(source.number ? { number: source.number } : {}),
    ...(phoneNumber ? { phoneNumber: String(phoneNumber) } : {}),
    five9InteractionId: five9InteractionId ? String(five9InteractionId) : null,
    five9CallId: five9CallId ? String(five9CallId) : null,
    five9SessionId: five9SessionId ? String(five9SessionId) : null,
    ani: source.ANI || source.ani || source.from || null,
    dnis: source.DNIS || source.dnis || source.to || null,
    state: source.callState || source.state || source.status || null,
    direction: normalizeDirection(source),
    ...(Object.keys(screenpopCore).length ? { screenpopCore } : {}),
    raw: root
  };
}
